import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';
import _ from 'lodash';

import { openModalAction } from '../../actions/modal';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/PageHeader/Button';
import { TextInput } from '../../components/PageHeader/TextInput';
import { DATE_TIME_SPACE_12_UPPERCASE, HEADER_BUTTON_BLUE } from '../../constants';
import {
  getSearchParamFromUrl,
  hydrateTableRequestFromUrl,
  saveSearchParamToUrl,
  useGlobalPageSize,
} from '../../utils/tableUtils';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import Strings from '../../Strings';
import { actions } from './redux/actions';
import { showStatus } from '../../utils';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { getFiltersForRequest, urlInjected } from '../../components/AdvancedFilters/helpers';
import { hasAnyPermissionInGroup, hasPermission, PERMISSION_GROUPS, PERMISSIONS } from '../../utils/userPermissions';
import { isSuperUser } from '../../utils/userRoles';
import { EDIT_ORG_TEMPLATE_MODAL_NAME } from '../../modals/EditOrganizationTemplateModal';
import { CREATE_ORG_TEMPLATE_MODAL_NAME } from '../../modals/CreateOrganizationTemplateModal';


function Organization(props) {
  const globalPageSize = useGlobalPageSize();
  const pageRequest = React.useRef({ offset: 0, limit: globalPageSize, search: getSearchParamFromUrl() });
  const { isLoading, organizations, pagination, templates, templatesLoading } = props;
  const {
    getOrganizations,
    getOrgTemplates,
    onAddOrganization,
    onEditOrganization,
    onNavigate,
    openConfirmModal,
    openEditTemplateModal,
    openCreateTemplateModal,
  } = props;
  const [search, setSearch] = React.useState(getSearchParamFromUrl());
  const canManageOrganizations = hasPermission(PERMISSIONS.ORGANIZATION_MANAGEMENT);

  React.useEffect(() => {
    hydrateTableRequestFromUrl(pageRequest.current, globalPageSize);
    pageRequest.current.search = getSearchParamFromUrl();
    getOrgTemplates(pageRequest.current);
  }, [getOrgTemplates]);

  const getHeaderComponents = () => {
    return (
      <>
        <TextInput
          class="search"
          placeholder={Strings.search}
          value={search}
          onChange={e => {
            const value = e.target.value;
            setSearch(value);
            onSearchChange(value);
          }}
        />
        <Button class={HEADER_BUTTON_BLUE} onClick={openCreateTemplateModal} title={Strings.createTemplate} />
      </>
    );
  };

  const onSearchChange = React.useMemo(
    () => _.debounce(query => {
      pageRequest.current.offset = 0;
      pageRequest.current.search = query;
      saveSearchParamToUrl(query);
      getOrgTemplates(pageRequest.current);
    }, 500),
    [getOrgTemplates],
  );

  const onOffsetChange = (offset, limit) => {
    pageRequest.current.offset = offset;
    if (limit) {
      pageRequest.current.limit = limit;
    }
    getOrgTemplates(pageRequest.current);
  };

  const onSortingChange = sorting => {
    if (sorting && sorting.length > 0) {
      pageRequest.current.offset = 0;
      pageRequest.current.sortColumn = sorting[0].id;
      pageRequest.current.sortType = sorting[0].desc ? 'descending' : 'ascending';
      getOrgTemplates(pageRequest.current);
    }
  };

  const onPageSizeChange = newSize => {
    pageRequest.current.offset = 0;
    pageRequest.current.limit = newSize;
    getOrgTemplates(pageRequest.current);
  };

  const onTemplateClicked = (template) => {
    if (!template?.uuid) {
      return;
    }
    const templateName = template?.name ? encodeURIComponent(template.name) : 'template';
    onNavigate(`/admin/templates/${encodeURIComponent(template.uuid)}/${templateName}`);
  };

  const onTemplateEdit = (template) => {
    if (!template?.uuid) {
      return;
    }
    openEditTemplateModal(template);
  };

  const onTemplateDelete = (id, template) => {
    if (!template?.uuid) {
      return;
    }
    const data = {
      title: (
        <span>
          {Strings.deleteTemplateWarning} <b>{template.name}</b>?
        </span>
      ),
      onConfirmAction: actions.deleteOrgTemplate(template.uuid),
      onCancelAction: null,
      confirmPostAction: actions.getOrgTemplates(),
    };
    openConfirmModal(data);
  };

  const formatTemplateDateTime = value => {
    if (!value) {
      return '-';
    }
    const parsed = moment(value);
    if (Number.isNaN(parsed.unix())) {
      return value;
    }
    return parsed.format(DATE_TIME_SPACE_12_UPPERCASE);
  };

  const columns = [
    columnHelper.accessor('name', {
      header: Strings.name,
    }),
    columnHelper.accessor('owner', {
      header: Strings.owner,
    }),
    columnHelper.accessor('created_at', {
      header: Strings.createdAt,
      cell: info => formatTemplateDateTime(info.getValue()),
    }),
    columnHelper.accessor('uuid', {
      header: Strings.uuid,
    }),
  ];

  const buttons = [
    {
      icon: 'edit',
      onClick: (_, row) => onTemplateEdit(row),
      text: Strings.edit,
    },
    {
      icon: 'delete',
      onClick: onTemplateDelete,
      text: Strings.delete,
    },
  ];


  return (
    <>
      <PageHeader
        right={getHeaderComponents()}
      />
      <AdvancedFiltersBar />
      <Table3
        isLoading={templatesLoading}
        name="organization-templates"
        data={templates}
        columns={columns}
        buttons={buttons}
        onRowClick={onTemplateClicked}
        enableColumnFiltering={false}
      />
    </>
  );
}

const mapStateToProps = state => {
  const { organizations } = state.superUser;
  const orgTemplates = state.superUser.organizations;

  return {
    organizations: organizations && organizations?.organization,
    isLoading: organizations && organizations?.isLoading,
    pagination: organizations && organizations?.pagination,
    filters: state.entities.advancedFilters.filters.items,
    templates: orgTemplates?.templates,
    templatesLoading: orgTemplates?.templatesLoading,
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: path => dispatch(push(path)),
  getOrgTemplates: pageRequest => dispatch(actions.getOrgTemplates(pageRequest)),
  openEditTemplateModal: template =>
    dispatch(
      openModalAction(EDIT_ORG_TEMPLATE_MODAL_NAME, {
        template,
      }),
    ),
  openCreateTemplateModal: () => dispatch(openModalAction(CREATE_ORG_TEMPLATE_MODAL_NAME)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Organization);
